import type { QuestLevel } from "@outpost/shared";
import {
  GTM_MISSION_PACK_VERSION,
  listMissionQuests,
  listMissions,
  type AutomationLevel,
} from "@outpost/shared";

export type CatalogGlyph = "assign" | "landing" | "compare" | "tool";

/** Channels that appear on mission-pack quests (browse filter only). */
export type QuestChannel =
  | "SETUP"
  | "SEO"
  | "SOME"
  | "VIDEO"
  | "ARTICLE"
  | "LANDING"
  | "COLD"
  | "LEARN"
  | "OUTCOME";

export type SeedQuest = {
  id: string;
  minLevel: number;
  title: string;
  evidence: string;
  channel: QuestChannel;
  requires?: string;
  glyph?: CatalogGlyph;
  brief?: string;
  steps?: string[];
  executableBy?: "human" | "agent" | "either";
  funnelStage?: string;
  complexity?: 1 | 2 | 3 | 4 | 5;
  executionXp?: number;
  evidenceBonusXp?: number;
  totalXp?: number;
  prerequisites?: string[];
  catalogAction?: string;
  missionId?: string;
  missionCode?: string;
  missionName?: string;
  missionShortName?: string;
  automationLevel?: AutomationLevel;
  timebox?: string;
  successSignals?: string[];
  failureSignals?: string[];
  humanOnlyJudgment?: string[];
  nextQuestId?: string | null;
  pack?: string;
};

/** Active catalog version = GTM mission pack only. */
export const CATALOG_VERSION = GTM_MISSION_PACK_VERSION;
export const MISSION_PACK_VERSION = GTM_MISSION_PACK_VERSION;
export const GTM_MISSIONS = listMissions();

/** Sole player-facing catalog: M01–M07 (54 quests). Legacy channel seeds archived. */
export const SEED_QUEST_CATALOG: SeedQuest[] = listMissionQuests().map((q) => ({
  id: q.id,
  minLevel: q.minLevel,
  title: q.title,
  evidence: q.proof,
  channel: q.channel as QuestChannel,
  requires: q.requires ?? undefined,
  brief: q.brief,
  steps: q.steps,
  executableBy: q.executableBy,
  funnelStage: q.funnelStage,
  complexity: q.complexity,
  executionXp: q.executionXp,
  evidenceBonusXp: q.evidenceBonusXp,
  totalXp: q.totalXp,
  prerequisites: q.prerequisites,
  catalogAction: q.action,
  missionId: q.missionId,
  missionCode: q.missionCode,
  missionName: q.missionName,
  missionShortName: q.missionShortName,
  automationLevel: q.automationLevel,
  timebox: q.timebox,
  successSignals: q.successSignals,
  failureSignals: q.failureSignals,
  humanOnlyJudgment: q.humanOnlyJudgment,
  nextQuestId: q.nextQuestId,
  pack: q.pack,
}));

export function apiTierFromMinLevel(minLevel: number): QuestLevel {
  if (minLevel <= 10) return 1;
  if (minLevel <= 20) return 2;
  if (minLevel <= 40) return 3;
  return 4;
}

const CHANNEL_BRIEF: Record<QuestChannel, string> = {
  SETUP: "Connect what you need so the lookout can see.",
  SEO: "Check whether people can still find you.",
  SOME: "Send a clear signal on the channels you already tend.",
  VIDEO: "Tell the story once; cut pieces others can follow.",
  ARTICLE: "Write it down so the path and the offer stay clear.",
  LANDING: "Give intent a place to land — use-case, compare, or tool.",
  COLD: "Walk one reusable path to strangers; stop rules keep it honest.",
  LEARN: "Ask people what they actually needed.",
  OUTCOME: "Only a named result counts — proof the company moved.",
};

export function questBrief(seed: SeedQuest): string {
  if (seed.brief?.trim()) return seed.brief.trim();
  return `${CHANNEL_BRIEF[seed.channel]} Next: ${seed.title}.`;
}

export function questSteps(seed: SeedQuest): string[] {
  if (seed.steps?.length) return seed.steps;
  const steps = [
    `Do the work: ${seed.title}.`,
    `Capture proof: ${seed.evidence}.`,
    "Paste the proof URL in My active, then confirm it is true.",
  ];
  if (seed.prerequisites?.length) {
    steps.unshift(`Readiness: ${seed.prerequisites.join(" · ")}.`);
  }
  if (seed.requires) {
    steps.unshift(`Finish ${seed.requires} first.`);
  }
  if (seed.timebox) {
    steps.unshift(`Timebox: ${seed.timebox}.`);
  }
  steps.unshift(`Lookout guidance: recommended around L${seed.minLevel}.`);
  return steps;
}

export function questXp(seed: SeedQuest): number {
  if (typeof seed.totalXp === "number" && seed.totalXp > 0) return seed.totalXp;
  return XP_PER_QUEST;
}

export const LOOKOUT_MAX_LEVEL = 60;
export const XP_PER_QUEST = 40;
export const DECADE_SIZE = 10;

/** Channels present on the active mission pack (for refine chips). */
const ACTIVE_CHANNELS = new Set(
  SEED_QUEST_CATALOG.map((s) => s.channel),
);

export const CHANNEL_LABELS: Record<QuestChannel, string> = {
  SETUP: "Setup",
  SEO: "SEO",
  SOME: "Social",
  VIDEO: "Video",
  ARTICLE: "Article",
  LANDING: "Landing",
  COLD: "Cold email",
  LEARN: "Learn",
  OUTCOME: "Outcome",
};

export const CHANNEL_FILTERS: Array<QuestChannel | "ALL"> = [
  "ALL",
  ...(
    ["LEARN", "COLD", "OUTCOME", "ARTICLE", "SETUP", "SEO", "SOME", "VIDEO", "LANDING"] as QuestChannel[]
  ).filter((ch) => ACTIVE_CHANNELS.has(ch)),
];

/**
 * Ops gap → nearest mission-pack quest (legacy OP-/AG- templates archived).
 * Voice: point at a concrete next step, not a missing catalog id.
 */
export function opsGapToSeedId(gap: string): string {
  switch (gap) {
    case "stale_evidence":
      return "M06-A0-01";
    case "failed_execution":
      return "M04-A0-01";
    case "improve":
      return "M06-A0-02";
    case "uncovered":
    case "unassessed":
    default:
      return "M01-A0-01";
  }
}

export function decadeStart(level: number): number {
  const clamped = Math.min(LOOKOUT_MAX_LEVEL, Math.max(1, level));
  return Math.floor((clamped - 1) / DECADE_SIZE) * DECADE_SIZE + 1;
}

export function decadeEnd(start: number): number {
  return Math.min(LOOKOUT_MAX_LEVEL, start + DECADE_SIZE - 1);
}

export function allDecadeStarts(): number[] {
  const starts: number[] = [];
  for (let s = 1; s <= LOOKOUT_MAX_LEVEL; s += DECADE_SIZE) starts.push(s);
  return starts;
}

export {
  lookoutLevelFromXp,
  xpFloorForLevel,
  lookoutXpProgress,
  awardAmountForTemplate,
  XP_COMPLEXITY_BASE,
  XP_RULE_VERSION_H1,
  XP_RULE_VERSION_H2,
} from "@outpost/shared";

export function isMinLevelLocked(minLevel: number, lookoutLevel: number): boolean {
  return minLevel > lookoutLevel + 1;
}

export function catalogGlyphSrc(seed: SeedQuest, locked: boolean): string {
  if (locked) return "/brand/outpost16-quest-level-lock.png";
  switch (seed.glyph) {
    case "landing":
      return "/brand/outpost16-quest-type-landing.png";
    case "compare":
      return "/brand/outpost16-quest-type-compare.png";
    case "tool":
      return "/brand/outpost16-quest-type-tool.png";
    default:
      return "/brand/outpost16-quest-assign-flag.png";
  }
}

export function seedDescription(seed: SeedQuest): string {
  const bits = [
    `[${seed.id}] ${seed.channel} · ${seed.evidence}`,
    "",
  ];
  if (seed.missionName) {
    bits.push(
      `Mission: ${seed.missionName}${seed.automationLevel ? ` · ${seed.automationLevel}` : ""}`,
      "",
    );
  }
  bits.push(questBrief(seed), "", ...questSteps(seed).map((s, i) => `${i + 1}. ${s}`));
  if (seed.totalXp) {
    bits.push("", `Suggested XP: ${seed.totalXp} (execution + evidence).`);
  }
  return bits.join("\n");
}

export function matchSeedInQuests<
  T extends { title: string; description: string | null },
>(seed: SeedQuest, quests: T[]): T | undefined {
  return quests.find(
    (q) =>
      q.title === seed.title ||
      (q.description?.includes(`[${seed.id}]`) ?? false),
  );
}

export function findSeedForQuest(quest: {
  title: string;
  description: string | null;
}): SeedQuest | undefined {
  return SEED_QUEST_CATALOG.find(
    (s) =>
      s.title === quest.title ||
      (quest.description?.includes(`[${s.id}]`) ?? false),
  );
}

export function resolveCatalogId(id: string): string {
  return id;
}

/** Legacy channel/L1–L4 id shape — must never appear in SEED_QUEST_CATALOG. */
export const LEGACY_QUEST_ID_RE =
  /^(SU|SEO|SM|VI|AR|LA|CE|LE|OU|AG|OP|INV|L\d|RV|RF)-\d{2}$/i;
